import { defineComponent as _defineComponent } from 'vue'
import { GroupByTypes } from '@padlet/arvo'
import device from 'bits/device'
import sectionSorter from 'bits/section_sorter'
import type { Group } from 'bits/surface_posts_grouping_helper'
import { vDel } from 'bits/vue'
import { storeToRefs } from 'pinia'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfacePermissionsStore } from 'pinia/surface_permissions'
import { useSurfacePostsStore } from 'pinia/surface_posts'
import { useSurfaceSectionsStore } from 'pinia/surface_sections'
import { useSurfaceWishArrangementStore } from 'pinia/surface_wish_arrangement'
import { computePostIndexFromPost } from 'surface/sorter'
import type { Id } from 'types'
import type Vue from 'vue'
import type { Ref } from 'vue'
import { computed, defineAsyncComponent, ref } from 'vue'
import SurfacePostGroupHeaderSection from 'vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostsDraggablePad from 'vuecomponents/SurfacePostsDraggablePad.vue'
import SurfacePost from 'vuecomponents/surface_post.vue'
import { useDragSortableInGroups } from 'vuecomposables/surface_posts_drag_sortable_in_groups'
import { useScrollToNewestPost } from 'vuecomposables/surface_scroll_to_newest_post'
import { useSurfaceSectionsDragSortable } from 'vuecomposables/surface_sections_drag_sortable'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfacePostsGrid',
  props: {
    reactionIdsByPostId: { default: () => ({}) },
    commentIdsByPostId: { default: () => ({}) }
  },
  setup(__props: any) {

const SurfaceAddSectionButton = defineAsyncComponent(() => import('vuecomponents/SurfaceAddSectionButton.vue'))



const surfacePostsStore = useSurfacePostsStore()
const surfaceSectionsStore = useSurfaceSectionsStore()
const { backgroundLuminance, dir, isReadOnly, isScreenshotMode, isPostSizeStandard, isPostSizeWide } = storeToRefs(
  useSurfaceStore(),
)
const { allSortedPostsByGroup, isAPostDragging, hoveredGroupIdWhenDraggingPost } = storeToRefs(surfacePostsStore)
const { canShowDraggingOverlay, shouldDisplayPost } = surfacePostsStore
const { isASectionDragging } = storeToRefs(surfaceSectionsStore)
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { sortedGroups, wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())

const rootElement: Ref<HTMLDivElement | null> = ref(null)

const overlayWidth = computed(() => {
  const postElementWidth = document.querySelector('.wish-v2')?.getBoundingClientRect().width
  // add extra width to the overlay so it extends beyond posts
  return postElementWidth ? postElementWidth + 12 : 0
})

const overlayTopPosition = computed(() => {
  const defaultTopMargin = 0
  if (hoveredGroupIdWhenDraggingPost.value === undefined) return defaultTopMargin
  const hoveredSectionHeaderHeight = postGroupHeaderHeights.value[hoveredGroupIdWhenDraggingPost.value]
  if (!hoveredSectionHeaderHeight) return defaultTopMargin

  if (hoveredSectionHeaderHeight === maxPostGroupHeaderHeight.value) return defaultTopMargin

  return maxPostGroupHeaderHeight.value - hoveredSectionHeaderHeight
})

function assignRootElementRef(v: Vue | null): void {
  rootElement.value = v?.$el as HTMLDivElement | null
}
useSurfaceSectionsDragSortable({
  onDragStart: () => {
    isASectionDragging.value = true
  },
  onDragEnd: ({ groupId }) => {
    isASectionDragging.value = false
    if (groupId) {
      const sectionElement = document.querySelector(`#section-${groupId}`) as HTMLElement | undefined
      if (!sectionElement) return
      const sortResult = sectionSorter.getNewIndex(sectionElement)
      if (!sortResult) return
      surfaceSectionsStore.sortSection(sortResult)
    }
  },
})
useDragSortableInGroups({
  onDragStart: ({ groupId }) => {
    isAPostDragging.value = true
    hoveredGroupIdWhenDraggingPost.value = groupId
  },
  onDragEnd: ({ postCid, postId, groupId }) => {
    isAPostDragging.value = false
    if (postCid && postId) {
      const sectionId = typeof groupId === 'number' && wishGroupBy.value === GroupByTypes.Section ? groupId : undefined
      const prevWish = document
        .querySelector(`#wish-${postId}`)
        ?.closest('.wish-wrapper')
        ?.previousElementSibling?.querySelector('.wish') as HTMLElement | undefined
      const nextWish = document
        .querySelector(`#wish-${postId}`)
        ?.closest('.wish-wrapper')
        ?.nextElementSibling?.querySelector('.wish') as HTMLElement | undefined
      const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${postId}`, {
        sectionId,
        prevId: prevWish?.id,
        nextId: nextWish?.id,
      })
      surfacePostsStore.sortPost({
        postCid,
        sortIndex: sortIndex ?? undefined,
        sectionId,
        gapSize,
      })
    }
    hoveredGroupIdWhenDraggingPost.value = undefined
  },
  format: 'grid',
})

useScrollToNewestPost()

const postGroupHeaderHeights = ref<Record<Group['id'], number>>({})

function updateMaxGroupHeaderHeight(groupId: Group['id'], { height }: DOMRectReadOnly): void {
  postGroupHeaderHeights.value = {
    ...postGroupHeaderHeights.value,
    [groupId]: height,
  }
}

/**
 * Make sure the group won't be scrolled horizontally on Safari
 * @param e
 */
function handleGroupScroll(e: Event) {
  if (!device.safari) return
  if (!isAPostDragging.value) return
  const target = e.target as HTMLElement
  if (target.scrollLeft > 0) {
    target.scrollLeft = 0
    e.preventDefault()
  }
}

const maxPostGroupHeaderHeight = computed(() => Math.max(...Object.values(postGroupHeaderHeights.value)))

return { __sfc: true,SurfaceAddSectionButton, surfacePostsStore, surfaceSectionsStore, backgroundLuminance, dir, isReadOnly, isScreenshotMode, isPostSizeStandard, isPostSizeWide, allSortedPostsByGroup, isAPostDragging, hoveredGroupIdWhenDraggingPost, canShowDraggingOverlay, shouldDisplayPost, isASectionDragging, canIModerate, sortedGroups, wishGroupBy, rootElement, overlayWidth, overlayTopPosition, assignRootElementRef, postGroupHeaderHeights, updateMaxGroupHeaderHeight, handleGroupScroll, maxPostGroupHeaderHeight, GroupByTypes, vDel, SurfacePostGroupHeaderSection, SurfacePostsDraggablePad, SurfacePost }
}

})